import { useState, useEffect, useRef } from "react";
import "./Editor.css";
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  RotateCcw,
  FlipHorizontal,
  FlipVertical,
} from "lucide-react";
import NoIndex from "../../../../../components/NoIndex";
import { ChevronDown } from "lucide-react";
import { ToastContainer } from "react-toastify";

const CANVAS_WIDTH = 720;
const CANVAS_HEIGHT = 320;

const fonts = [
  "Inter",
  "Roboto",
  "Poppins",
  "Arial",
  "Verdana",
  "Times New Roman",
  "Georgia",
  "Courier New",
  "Montserrat",
  "Open Sans",
];

const fontSizes = [12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 40, 48, 56, 64];

const SettingsPannel = ({ elements, setElements, selectedId }) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFontSizes(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [showFontSizes, setShowFontSizes] = useState(false);
  const element = elements.find((el) => el.id === selectedId);

  if (!element) {
    return null;
  }

  /* ---------------- UPDATE ---------------- */

  const update = (updates) => {
    setElements((prev) =>
      prev.map((el) => (el.id === selectedId ? { ...el, ...updates } : el)),
    );
  };

  /* ---------------- SAFE NUMBER ---------------- */

  const handleNumberChange = (key, value) => {
    update({
      [key]: value === "" ? undefined : Number(value),
    });
  };

  /* ---------------- GET PARENT ---------------- */

  const getParent = () => {
    if (element.parentBox) {
      return elements.find((el) => el.id === element.parentBox);
    }

    if (element.type === "box") {
      return {
        x: 0,
        y: 0,
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
      };
    }

    return null;
  };

  /* ---------------- ALIGN ---------------- */

  const align = (type) => {
    const parent = getParent();
    if (!parent) return;

    const w = element.width || 0;
    const h = element.height || 0;

    let x = element.x;
    let y = element.y;

    if (type === "left") x = parent.x;
    if (type === "center") x = parent.x + (parent.width - w) / 2;
    if (type === "right") x = parent.x + parent.width - w;

    if (type === "top") y = parent.y;
    if (type === "middle") y = parent.y + (parent.height - h) / 2;
    if (type === "bottom") y = parent.y + parent.height - h;

    update({ x, y });
  };

  /* ---------------- LAYER ---------------- */

  const bringFront = () => {
    if (element.type === "box") return;

    const maxZ = Math.max(...elements.map((el) => el.zIndex || 1));

    update({ zIndex: maxZ + 1 });
  };

  const sendBack = () => {
    const parentBox = elements.find((el) => el.id === element.parentBox);

    let minZ = 1;

    if (parentBox) {
      minZ = (parentBox.zIndex || 0) + 1;
    }

    update({
      zIndex: Math.max(minZ, (element.zIndex || 2) - 1),
    });
  };
  const autoFitText = (size) => {
    if (!element.parentBox) return;

    const parent = elements.find((el) => el.id === element.parentBox);

    if (!parent) return;

    const maxWidth = parent.width - 20;
    const maxHeight = parent.height - 20;

    setElements((prev) =>
      prev.map((el) => {
        if (el.id !== element.id) return el;

        let newWidth = Math.min(maxWidth, size * el.text.length * 0.6);
        let newHeight = Math.min(maxHeight, size * 1.4);

        return {
          ...el,
          width: newWidth,
          height: newHeight,
        };
      }),
    );
  };

  /* ---------------- UI ---------------- */

  return (
    <>
    <NoIndex/>
    <ToastContainer/>
    
    <div className="settings-pannel montserrat-font-1">
      {/* POSITION */}

      <div className="settinggss-card montserrat-font-1 ">
        <h3>Position</h3>

        <div className="rowww montserrat-font-1">
          <div className="inputt-with-labbel">
            <span>X :</span>
            <input
              type="number"
              value={element.x ?? ""}
              onChange={(e) => handleNumberChange("x", e.target.value)}
            />
          </div>

          <div className="inputt-with-labbel">
            <span>Y :</span>
            <input
              type="number"
              value={element.y ?? ""}
              onChange={(e) => handleNumberChange("y", e.target.value)}
            />
          </div>
        </div>

        <label>Alignment</label>

        <div className="iconn-gridd montserrat-font-1">
          <button onClick={() => align("left")}>
            <AlignLeft size={16} />
          </button>
          <button onClick={() => align("center")}>
            <AlignCenter size={16} />
          </button>
          <button onClick={() => align("right")}>
            <AlignRight size={16} />
          </button>

          <button onClick={() => align("top")}>
            <AlignStartVertical size={16} />
          </button>
          <button onClick={() => align("middle")}>
            <AlignCenterVertical size={16} />
          </button>
          <button onClick={() => align("bottom")}>
            <AlignEndVertical size={16} />
          </button>
        </div>

        <label>Rotation</label>

        <div className="rotationn-rowww montserrat-font-1">
          <input
            type="number"
            value={element.rotation ?? 0}
            onChange={(e) => handleNumberChange("rotation", e.target.value)}
          />

          <button
            onClick={() => update({ rotation: (element.rotation || 0) + 90 })}
          >
            <RotateCcw size={16} />
          </button>

          <button
            onClick={() => update({ scaleX: (element.scaleX || 1) * -1 })}
          >
            <FlipHorizontal size={16} />
          </button>

          <button
            onClick={() => update({ scaleY: (element.scaleY || 1) * -1 })}
          >
            <FlipVertical size={16} />
          </button>
        </div>
      </div>

      {/* LAYOUT */}

      <div className="settinggss-card montserrat-font-1">
        <h3>Layout</h3>

        <label>Width / Height</label>

        <div className="rowww montserrat-font-1">
          <input
            type="number"
            value={element.width ?? ""}
            onChange={(e) => handleNumberChange("width", e.target.value)}
          />

          <input
            type="number"
            value={element.height ?? ""}
            onChange={(e) => handleNumberChange("height", e.target.value)}
          />
        </div>

        {/* Padding only for BOX */}

        {element.type === "box" && (
          <>
            <label>Padding</label>

            <input
              type="number"
              value={element.padding ?? ""}
              onChange={(e) => handleNumberChange("padding", e.target.value)}
            />
          </>
        )}
      </div>

      {/* APPEARANCE */}

      <div className="settinggss-card montserrat-font-1">
        <h3>Appearance</h3>

        {/* FONT SETTINGS */}

        {(element.type === "heading" || element.type === "button") && (
          <>
            <label>Font</label>

            <select
              className="fontt-selectt"
              value={element.fontFamily || "Inter"}
              onChange={(e) => update({ fontFamily: e.target.value })}
            >
              {fonts.map((font) => (
                <option key={font} value={font} style={{ fontFamily: font }}>
                  {font}
                </option>
              ))}
            </select>

            <label>Font Size</label>

            <div className="fontt-sizee-wordd montserrat-font-1" ref={dropdownRef}>
              <input
                type="number"
                value={parseInt(element.fontSize) || ""}
                onChange={(e) => {
                  const newSize = e.target.value;
                  update({ fontSize: newSize + "px" });
                  autoFitText(newSize);
                }}
              />

             <button
  type="button"
  className="fontt-sizee-arrowww"
  onMouseDown={(e) => e.stopPropagation()}
  onClick={() => setShowFontSizes((prev) => !prev)}
>
  <ChevronDown
    size={16}
    className={`transition-transform duration-200 ${
      showFontSizes ? "rotate-180" : ""
    }`}
  />
</button>

              {showFontSizes && (
                <div
                  className="fontt-sizee-dropdownn"
                  onClick={(e) => e.stopPropagation()}
                >
                  {fontSizes.map((size) => (
                    <div
                      key={size}
                      className={`fontt-sizee-optionn ${
                        parseInt(element.fontSize) === size ? "active" : ""
                      }`}
                      onClick={() => {
                        update({ fontSize: size + "px" });
                        autoFitText(size);
                        setShowFontSizes(false);
                      }}
                    >
                      {size}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* BUTTON SETTINGS */}

        {element.type === "button" && (
          <>
            <label>Button Text</label>

            <input
              type="text"
              value={element.text || ""}
              onChange={(e) => update({ text: e.target.value })}
            />

            <label>Button Link</label>

            <input
              type="text"
              value={element.link || ""}
              placeholder="https://example.com"
              onChange={(e) => update({ link: e.target.value })}
            />

            {/* BUTTON BACKGROUND */}

            <label>Button Background</label>

            <input
              type="color"
              value={element.background || "#000000"}
              onChange={(e) => update({ background: e.target.value })}
            />
          </>
        )}

        {/* TEXT STYLE */}

        {(element.type === "heading" || element.type === "button") && (
          <div className="fontt-roww">
            <button
              onClick={() =>
                update({
                  fontWeight: element.fontWeight === "bold" ? "normal" : "bold",
                })
              }
            >
              B
            </button>

            <button
              onClick={() =>
                update({
                  fontStyle:
                    element.fontStyle === "italic" ? "normal" : "italic",
                })
              }
            >
              I
            </button>

            <button
              onClick={() =>
                update({
                  textDecoration:
                    element.textDecoration === "underline"
                      ? "none"
                      : "underline",
                })
              }
            >
              U
            </button>
          </div>
        )}

        {/* COLOR + OPACITY */}

        <label>Color / Opacity</label>

        <div className="colorr-opacityy-boxx montserrat-font-1">
          <input
            className="colorr-squaree"
            type="color"
            value={
              element.type === "box"
                ? (element.background ?? "#ffffff")
                : (element.color ?? "#000000")
            }
            onChange={(e) => {
              const val = e.target.value;

              if (element.type === "box") {
                update({ background: val });
              } else {
                update({ color: val });
              }
            }}
          />
          <div className="opacityy-fieldd montserrat-font-1">
            <input
              type="number"
              min="0"
              max="100"
              value={
                element.opacityInput ?? Math.round((element.opacity ?? 1) * 100)
              }
              onChange={(e) => {
                let val = e.target.value;

                // allow empty input
                if (val === "") {
                  update({
                    opacityInput: "",
                  });
                  return;
                }

                let num = Math.min(100, Math.max(0, Number(val)));

                update({
                  opacity: num / 100,
                  opacityInput: num,
                });
              }}
              onBlur={() => {
                update({ opacityInput: undefined });
              }}
            />

            <span>%</span>
          </div>
        </div>

        <label>Border Radius</label>

        <input
          type="number"
          placeholder="0"
          value={element.borderRadius ?? ""}
          onChange={(e) => handleNumberChange("borderRadius", e.target.value)}
        />

        <label>Layer</label>

        <div className="layerr-roww montserrat-font-1">
          <button onClick={bringFront}>Bring Front</button>
          <button onClick={sendBack}>Send Back</button>
        </div>
      </div>
    </div>
    </>
  );
};

export default SettingsPannel;
